import { Routes } from '@/router/routes'

import { AppPermissions } from '@/types/enums/permissions/permissions'
import { Middleware } from '@/types/enums/middleware/middleware'

export const staffRoutes = [
  /**
   * Staff collection (grid)
   * URL: /manage-staff
   * Layout component: MainContent
   */
  {
    path: 'manage-staff',
    name: Routes.StaffIndex,
    component: () => import('@/pages/contexts/portal/staff/Index.vue'),
    meta: {
      pageTitle: 'Manage Staff',
      middleware: [
        Middleware.REQUIRES_AUTH,
        Middleware.TOGGLES_NAV_DRAWER,
      ],
      permission: AppPermissions.VIEW_STAFF_MANAGEMENT,
    },
  },

  /**
   * Staff member workspace (tabs)
   * URL base: /manage-staff/:staffId
   *
   * This route should render:
   * - the tab UI at the top
   * - a <RouterView/> where the active tab page renders
   */
  {
    path: 'manage-staff/:staffId',
    name: Routes.StaffMember,
    component: () => import('@/pages/contexts/portal/staff/_id/Index.vue'),
    meta: {
      pageTitle: 'Manage Staff Member',
      middleware: [
        Middleware.REQUIRES_AUTH,
        Middleware.TOGGLES_NAV_DRAWER,
      ],
      permission: AppPermissions.VIEW_STAFF_MANAGEMENT,
    },
    redirect: { name: Routes.StaffMemberOverview },
    children: [
      /**
       * Tab: Overview
       * URL: /manage-staff/:staffId/overview
       */
      {
        path: 'overview',
        name: Routes.StaffMemberOverview,
        component: () => import('@/pages/contexts/portal/staff/_id/overview/Index.vue'),
        meta: {
          pageTitle: 'Staff Overview',
          middleware: [
            Middleware.REQUIRES_AUTH,
            Middleware.TOGGLES_NAV_DRAWER,
          ],
          permission: AppPermissions.MANAGE_STAFF,
        },
      },

      /**
       * Tab: Operational Bases
       * URL: /manage-staff/:staffId/operational-bases
       */
      {
        path: 'operational-bases',
        name: Routes.StaffMemberOperationalBases,
        component: () => import('@/pages/contexts/portal/staff/_id/operational-bases/Index.vue'),
        meta: {
          pageTitle: 'Staff Operational Bases',
          middleware: [
            Middleware.REQUIRES_AUTH,
            Middleware.TOGGLES_NAV_DRAWER,
          ],
          permission: AppPermissions.MANAGE_STAFF,
        },
      },

      /**
       * Tab: Role
       * URL: /manage-staff/:staffId/role
       */
      {
        path: 'role',
        name: Routes.StaffMemberRole,
        component: () => import('@/pages/contexts/portal/staff/_id/role/Index.vue'),
        meta: {
          pageTitle: 'Staff Role',
          middleware: [
            Middleware.REQUIRES_AUTH,
            Middleware.TOGGLES_NAV_DRAWER,
          ],
          permission: AppPermissions.MANAGE_STAFF,
        },
      },

      /**
       * Tab: Abilities
       * URL: /manage-staff/:staffId/abilities
       */
      {
        path: 'abilities',
        name: Routes.StaffMemberAbilities,
        component: () => import('@/pages/contexts/portal/staff/_id/abilities/Index.vue'),
        meta: {
          pageTitle: 'Staff Abilities',
          middleware: [
            Middleware.REQUIRES_AUTH,
            Middleware.TOGGLES_NAV_DRAWER,
          ],
          permission: AppPermissions.MANAGE_STAFF,
        },
      },

      /**
       * Tab: Experience Qualifications
       * URL: /manage-staff/:staffId/experience-qualifications
       */
      {
        path: 'experience-qualifications',
        name: Routes.StaffExperienceQualifications,
        component: () => import('@/pages/contexts/portal/staff/_id/experience-qualifications/Index.vue'),
        meta: {
          pageTitle: 'Staff Experience Qualifications',
          middleware: [
            Middleware.REQUIRES_AUTH,
            Middleware.TOGGLES_NAV_DRAWER,
          ],
          permission: AppPermissions.MANAGE_STAFF,
        },
      },

      /**
       * Tab: Permissions
       * URL: /manage-staff/:staffId/permissions
       */
      {
        path: 'permissions',
        name: Routes.StaffMemberPermissions,
        component: () => import('@/pages/contexts/portal/staff/_id/permissions/Index.vue'),
        meta: {
          pageTitle: 'Staff Permissions',
          middleware: [
            Middleware.REQUIRES_AUTH,
            Middleware.TOGGLES_NAV_DRAWER,
          ],
          permission: AppPermissions.EDIT_PERMISSIONS,
        },
      },

      /**
       * Tab: Employment Logs
       * URL: /manage-staff/:staffId/employment-logs
       */
      {
        path: 'employment-logs',
        name: Routes.StaffMemberEmploymentLogs,
        component: () => import('@/pages/contexts/portal/staff/_id/employment-logs/Index.vue'),
        meta: {
          pageTitle: 'Staff Employment Logs',
          middleware: [
            Middleware.REQUIRES_AUTH,
            Middleware.TOGGLES_NAV_DRAWER,
          ],
          permission: AppPermissions.MANAGE_STAFF,
        },
        children: [
          {
            path: 'create',
            name: Routes.StaffMemberEmploymentLogsCreate,
            component: () => import('@/pages/contexts/portal/staff/_id/employment-logs/Create.vue'),
            meta: {
              middleware: [
                Middleware.REQUIRES_AUTH,
                Middleware.TOGGLES_NAV_DRAWER,
              ],
              permission: AppPermissions.MANAGE_STAFF,
            },
          },
          {
            path: 'edit/:logId',
            name: Routes.StaffMemberEmploymentLogsEdit,
            component: () => import('@/pages/contexts/portal/staff/_id/employment-logs/Edit.vue'),
            meta: {
              middleware: [
                Middleware.REQUIRES_AUTH,
                Middleware.TOGGLES_NAV_DRAWER,
              ],
              permission: AppPermissions.MANAGE_STAFF,
            },
          },
        ],
      },
    ],
  },
]
